import React, { useState } from 'react';
import { useFormik } from 'formik';
import {
    Dialog,
    DialogContent,
    Grid,
    TextField,
    Button,
    Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import AppDialogTitle from '../../../ui/appDialogTitle';
import ValidFrameBasicInfo from '../frames/validFrameBasicInfoForm';
import ValidFrameCostAndPricingForm from '../frames/validFrameCostAndPricingForm';
import ValidContactPropertiesForm from './validContactPropertiesForm';
import contactValidationSchema, { initialValuesContactForm } from './contactValidationSchema';
import { ValidDataService } from '../../../../service/validDataService';
import labels from './labels.json';
import AttachmentUploader from '../../../common/attachmentUploader';
import { ACCEPTED_IMAGE_FILE_FORMAT, formatFileSize } from '../../../../utils';
import SectionTitle from '../../../ui/sectionTitle';
import SimpleDatePicker from '../../../ui/simpleDatePicker';
import SupplierListDialog from '../../ValidData/validSupplier/supplierListDialog';
import BrandListDialog from '../../ValidData/validSupplier/brandListDialog';
import validLookup from '../../../../utils/validLookup';

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_NO_OF_FILES = 10;

export default function ValidContactInputForm({ open, onClose, onRecordAdded }) {
    const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
    const [brandDialogOpen, setBrandDialogOpen] = useState(false);

    const formik = useFormik({
        initialValues: initialValuesContactForm,
        validationSchema: contactValidationSchema,
        onSubmit: (values) => {
            onSubmit(values);
        },
    });
    const { values, setFieldValue, handleChange, handleSubmit, touched } = formik;

    const onSubmit = async (values) => {
        formik.setSubmitting(true);
        try {
            const formattedValues = {
                ...values,
                supplierCost: values.supplierCost === '' ? null : parseFloat(values.supplierCost),
                landingCost: values.landingCost === '' ? null : parseFloat(values.landingCost),
                listPrice: values.listPrice === '' ? null : parseFloat(values.listPrice),
                photos: values.photos === '' ? null : values.photos,
                type: 'contacts',
                locationId: values.location.id,
                profit: values.profit === '' ? null : parseFloat(values.profit),
                profitPercentage: values.profitPercentage === '' ? null : parseFloat(values.profitPercentage),
                lastOrdered: values.lastOrdered === '' ? null : values.lastOrdered,
                lastReceived: values.lastReceived === '' ? null : values.lastReceived,
                lastSold: values.lastSold === '' ? null : values.lastSold,
                totalSold: values.totalSold === '' ? null : parseInt(values.totalSold, 10),
                returned: values.returned === '' ? null : parseInt(values.returned, 10),
                quantityOnOrder: values.quantityOnOrder === '' ? null : parseInt(values.quantityOnOrder, 10),
                quantityOnHand: values.quantityOnHand === '' ? null : parseInt(values.quantityOnHand, 10),
                properties: {
                    ...values.properties,
                    sphere: values.properties.sphere === '' ? null : parseFloat(values.properties.sphere),
                    cyl: values.properties.cyl === '' ? null : parseFloat(values.properties.cyl),
                    axis: values.properties.axis === '' ? null : parseFloat(values.properties.axis),
                    add: values.properties.add === '' ? null : parseFloat(values.properties.add),
                    bc: values.properties.bc === '' ? null : parseFloat(values.properties.bc),
                    dia: values.properties.dia === '' ? null : parseFloat(values.properties.dia),
                    packSize: values.properties.packSize === '' ? null : parseInt(values.properties.packSize, 10),
                    color: values.properties.color,
                    expirationDate: values.properties.expirationDate === '' ? null : values.properties.expirationDate,
                },
            };

            // Call the API to add the inventory
            const response = await ValidDataService.addInventory(formattedValues);
            const { code, data, message } = response;

            formik.setSubmitting(false);

            if (code === 0) {
                toast.success(message);
                formik.resetForm();
                onRecordAdded(data);
            } else {
                toast.error(message);
            }
        } catch (error) {
            console.error(error);
            formik.setSubmitting(false);
            toast.error('Failed to add record');
        }
    };

    const handleSupplierSelect = (supplier) => {
        setFieldValue('supplierId', supplier.id);
        setFieldValue('supplierName', supplier.supplierName);
        // reset the brand
        setFieldValue('brand', '')
    };

    const handleBrandSelect = (brand) => {
        //setFieldValue('supplierId', supplier.id);
        setFieldValue('brand', brand.name);
        setFieldValue('brandId', brand.id)
    };

    return (
        <Dialog open={open} maxWidth={'lg'} fullWidth>
            <AppDialogTitle title="Add Contact Lens" onClose={onClose} />
            <DialogContent>
                <form onSubmit={formik.handleSubmit}>
                    <Grid container spacing={2}>
                        <ValidFrameBasicInfo
                            formik={formik}
                            setBrandDialogOpen={setBrandDialogOpen}
                            setSupplierDialogOpen={setSupplierDialogOpen}
                            labels={labels}
                        />
                        <Grid item xs={12} sm={6}>
                            <ValidFrameCostAndPricingForm formik={formik} />
                        </Grid>
                        {/* <Grid item xs={12} sm={6}>
                            <ValidContactPropertiesForm formik={formik} />
                        </Grid> */}

                        {/* Section 5: Inventory and Status */}
                        {/*<Grid item xs={12}>
                            <SectionTitle title="Inventory and Status" />
                        </Grid>
                        <Grid item xs={12} sm={3} mt={1}>
                            <SimpleDatePicker
                                label={"Last Ordered"}
                                onChange={(value) => setFieldValue('lastOrdered', value)}
                                value={values.lastOrdered}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3} mt={1}>
                            <SimpleDatePicker
                                label={"Last Received"}
                                onChange={(value) => setFieldValue('lastReceived', value)}
                                value={values.lastReceived}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3} mt={1}>
                            <SimpleDatePicker
                                label={"Last Sold"}
                                onChange={(value) => setFieldValue('lastSold', value)}
                                value={values.lastSold}
                            />
                        </Grid>
                        <Grid item xs={12} sm={2} mt={1}>
                            <TextField
                                value={values.totalSold}
                                onChange={handleChange}
                                name="totalSold"
                                label="Total Sold"
                                type="number"
                                size='small'
                                fullWidth
                                disabled
                            />
                        </Grid>
                        <Grid item xs={12} sm={2} mt={1}>
                            <TextField
                                value={values.returned}
                                onChange={handleChange}
                                name="returned"
                                label="Returned"
                                type="number"
                                size='small'
                                fullWidth
                                disabled
                            />
                        </Grid>
                        <Grid item xs={12} sm={2} mt={1}>
                            <TextField
                                value={values.quantityOnOrder}
                                onChange={handleChange}
                                name="quantityOnOrder"
                                label="Quantity On Order"
                                type="number"
                                size='small'
                                fullWidth
                                error={touched.quantityOnOrder && Boolean(formik.errors.quantityOnOrder)}
                                helperText={touched.quantityOnOrder && formik.errors.quantityOnOrder}
                            />
                        </Grid>
                        <Grid item xs={12} sm={2} mt={1}>
                            <TextField
                                value={values.quantityOnHand}
                                onChange={handleChange}
                                name="quantityOnHand"
                                label="Quantity On Hand"
                                type="number"
                                size='small'
                                fullWidth
                                error={touched.quantityOnHand && Boolean(formik.errors.quantityOnHand)}
                                helperText={touched.quantityOnHand && formik.errors.quantityOnHand}
                            />
                        </Grid>

                        <Grid item xs={12} mt={1}>
                            <TextField
                                value={values.notes}
                                onChange={handleChange}
                                name="notes"
                                label="Notes"
                                multiline
                                rows={4}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant='caption'>Logo (JPEG/PNG files under {formatFileSize(MAX_FILE_SIZE)}). Max files allowed {MAX_NO_OF_FILES}</Typography>
                            <AttachmentUploader
                                values={formik.values.photos}
                                onUpload={(files) => {
                                    formik.setFieldValue('photos', files);
                                    formik.setFieldTouched('photos', true);
                                }}
                                accept={
                                    ACCEPTED_IMAGE_FILE_FORMAT.reduce((acc, mimeType) => {
                                        acc[mimeType] = [];
                                        return acc;
                                    }, {})
                                }
                                maxFiles={MAX_NO_OF_FILES}
                                maxSize={MAX_FILE_SIZE}
                            />
                        </Grid> */}
                        <Grid item xs={12}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={formik.isSubmitting}
                            >
                                Create
                            </Button>
                        </Grid>
                    </Grid>
                </form>
                <SupplierListDialog
                    open={supplierDialogOpen}
                    onClose={() => setSupplierDialogOpen(false)}
                    onSelect={handleSupplierSelect} // Pass setFieldValue
                    supplierType={validLookup.supplierType.contacts}
                />
                <BrandListDialog
                    open={brandDialogOpen}
                    onClose={() => setBrandDialogOpen(false)}
                    onSelect={handleBrandSelect}
                    supplierId={values.supplierId}
                    type='contacts'
                    initialFilter={values.brand}
                />                
            </DialogContent>
        </Dialog>
    );
}